import { designTokens, RiDeleteBinLine } from "@itandi/itandi-bb-ui";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { FormRow } from "@itandi/itandi-bb-ui/FormRow";
import { Heading } from "@itandi/itandi-bb-ui/Heading";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { InputRadio } from "@itandi/itandi-bb-ui/InputRadio";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useState } from "react";
import {
	Controller,
	useFormContext,
	useFormState,
	useWatch
} from "react-hook-form";
import { ESTIMATION_TYPE } from "~/models/DealConstruction";
import type {
	ChargePers,
	DealEstimationOcrFormInput
} from "~/models/DealEstimationOcrForm";
import {
	CHARGE_PER_KEYS,
	calculateDealEstimationOcrItemAmount,
	complementChargePers,
	DEAL_ESTIMATION_OCR_MAX_CHARGE_PER,
	DEAL_ESTIMATION_OCR_MIN_CHARGE_PER,
	DEAL_ESTIMATION_OCR_MIN_UNIT,
	DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER_ERROR_MESSAGE
} from "~/models/DealEstimationOcrForm";
import { toThousandSeparator } from "~/utils/CurrencySeparator";
import { isEmpty } from "~/utils/StringUtil";

type Props = {
	disabled?: boolean;
	index: number;
	isConfirmed: boolean;
	onConfirmationChange: () => void;
	onRemove: (index: number) => void;
};

const toNumberOrNull = (value: string): number | null =>
	value === "" ? null : Number(value);

export function EstimationOcrItemForm({
	disabled,
	index,
	isConfirmed,
	onConfirmationChange,
	onRemove
}: Props) {
	const fieldPrefix = `items.${index}` as const;
	const { control, setValue, trigger } =
		useFormContext<DealEstimationOcrFormInput>();
	const { errors } = useFormState({ control, name: fieldPrefix });
	const item = useWatch({ control, name: fieldPrefix });
	const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
		useState(false);
	const itemErrors = errors.items?.[index];
	const amount = calculateDealEstimationOcrItemAmount(item);
	const itemName = item.otherName || "(未入力)";
	const headingTitle = isEmpty(item.point)
		? itemName
		: `${item.point}　${itemName}`;
	const hasTotalChargePerError =
		itemErrors?.renteeChargePer?.message ===
		DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER_ERROR_MESSAGE;

	// 変更中のフィールド自身が空欄の場合は、入力し直す途中の可能性があるため補完しない
	const completeChargePer = (changedKey: keyof ChargePers) => {
		const completedChargePers = complementChargePers(item);
		const isChangedValueEmpty = item[changedKey] === null;
		const keysToRevalidate: (keyof ChargePers)[] = [];
		CHARGE_PER_KEYS.forEach((key) => {
			if (key === changedKey) {
				keysToRevalidate.push(key);
			} else {
				if (!isChangedValueEmpty && completedChargePers[key] !== item[key]) {
					setValue(`${fieldPrefix}.${key}`, completedChargePers[key], {
						shouldValidate: false
					});
				}
				// 合計エラーを各フィールドの変更時に反映しつつ、
				// 未入力のフィールドには触れる前から必須エラーを表示しない
				const resultingValue = isChangedValueEmpty
					? item[key]
					: completedChargePers[key];
				if (resultingValue !== null) {
					keysToRevalidate.push(key);
				}
			}
		});
		void trigger(
			keysToRevalidate.map((key) => `${fieldPrefix}.${key}` as const)
		);
	};

	return (
		<div>
			<Heading
				as="h3"
				mb={
					isConfirmed
						? designTokens["spacing/none"]
						: designTokens["spacing/xs"]
				}
				rightArea={
					<Flex alignItems="center" gap={designTokens["spacing/s"]}>
						<InputCheckBox
							checked={isConfirmed}
							disabled={disabled}
							label="確認済み"
							onChange={onConfirmationChange}
						/>
						<Buttonv2
							color="danger"
							disabled={disabled}
							LeftIcon={RiDeleteBinLine}
							onClick={() => {
								setIsDeleteConfirmModalOpen(true);
							}}
							size="s"
							variant="secondary"
						>
							削除
						</Buttonv2>
					</Flex>
				}
			>
				{headingTitle}
			</Heading>
			{isDeleteConfirmModalOpen ? (
				<Modalv2
					isOpen
					onClose={() => {
						setIsDeleteConfirmModalOpen(false);
					}}
					size="s"
				>
					<ModalHeader title="明細の削除" />
					<ModalBody>
						<Paragraph>
							{item.otherName
								? `「${item.otherName}」を削除します。`
								: "この明細を削除します。"}
						</Paragraph>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Buttonv2
								onClick={() => {
									setIsDeleteConfirmModalOpen(false);
								}}
								variant="secondary"
							>
								キャンセル
							</Buttonv2>
						}
						rightArea={
							<Buttonv2
								color="danger"
								onClick={() => {
									onRemove(index);
									setIsDeleteConfirmModalOpen(false);
								}}
								variant="primary"
							>
								削除
							</Buttonv2>
						}
					/>
				</Modalv2>
			) : null}
			{!isConfirmed && (
				<FormGroup variant="vertical">
					<FormControl
						chip="必須"
						isInvalid={!!itemErrors?.otherName}
						label="施工項目名"
					>
						<Controller
							control={control}
							name={`${fieldPrefix}.otherName`}
							render={({ field }) => (
								<Input
									maxLength={255}
									name={field.name}
									onBlur={field.onBlur}
									onChange={(event) => {
										field.onChange(event.target.value);
									}}
									placeholder="例）壁クロス張替え"
									ref={field.ref}
									value={field.value}
								/>
							)}
						/>
						<FormControlText error={itemErrors?.otherName?.message} />
					</FormControl>
					<FormRow>
						<FormControl label="提案ラベル">
							<Controller
								control={control}
								name={`${fieldPrefix}.estimationType`}
								render={({ field }) => (
									<Flex gap={designTokens["spacing/s"]}>
										<InputRadio
											checked={field.value === ESTIMATION_TYPE.NONE}
											label="なし"
											name={field.name}
											onChange={() => {
												field.onChange(ESTIMATION_TYPE.NONE);
											}}
											value={ESTIMATION_TYPE.NONE}
										/>
										<InputRadio
											checked={field.value === ESTIMATION_TYPE.PROPOSAL}
											label="あり"
											name={field.name}
											onChange={() => {
												field.onChange(ESTIMATION_TYPE.PROPOSAL);
											}}
											value={ESTIMATION_TYPE.PROPOSAL}
										/>
									</Flex>
								)}
							/>
						</FormControl>
						<FormControl isInvalid={!!itemErrors?.point} label="施工箇所">
							<Controller
								control={control}
								name={`${fieldPrefix}.point`}
								render={({ field }) => (
									<Input
										maxLength={255}
										name={field.name}
										onBlur={field.onBlur}
										onChange={(event) => {
											field.onChange(event.target.value);
										}}
										placeholder="例）リビング"
										ref={field.ref}
										value={field.value}
									/>
								)}
							/>
							<FormControlText error={itemErrors?.point?.message} />
						</FormControl>
					</FormRow>
					<FormRow>
						<FormControl
							chip="必須"
							isInvalid={!!itemErrors?.unitAmount}
							label="単価"
						>
							<Controller
								control={control}
								name={`${fieldPrefix}.unitAmount`}
								render={({ field }) => (
									<Input
										name={field.name}
										onBlur={field.onBlur}
										onChange={(event) => {
											field.onChange(toNumberOrNull(event.target.value));
										}}
										placeholder="単価"
										ref={field.ref}
										step={1}
										type="number"
										value={field.value ?? ""}
									/>
								)}
							/>
							<FormControlText error={itemErrors?.unitAmount?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!itemErrors?.unit}
							label="数量"
						>
							<Controller
								control={control}
								name={`${fieldPrefix}.unit`}
								render={({ field }) => (
									<Input
										min={DEAL_ESTIMATION_OCR_MIN_UNIT}
										name={field.name}
										onBlur={field.onBlur}
										onChange={(event) => {
											field.onChange(toNumberOrNull(event.target.value));
										}}
										placeholder="数量"
										ref={field.ref}
										step="any"
										type="number"
										value={field.value ?? ""}
									/>
								)}
							/>
							<FormControlText error={itemErrors?.unit?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!itemErrors?.unitName}
							label="単位"
						>
							<Controller
								control={control}
								name={`${fieldPrefix}.unitName`}
								render={({ field }) => (
									<Input
										maxLength={255}
										name={field.name}
										onBlur={field.onBlur}
										onChange={(event) => {
											field.onChange(event.target.value);
										}}
										placeholder="例）式"
										ref={field.ref}
										value={field.value}
									/>
								)}
							/>
							<FormControlText error={itemErrors?.unitName?.message} />
						</FormControl>
						<FormControl label="金額">
							<Label>
								{amount === null
									? "-"
									: `¥${toThousandSeparator(amount) ?? "0"}`}
							</Label>
							<FormControlText help="自動計算されます" />
						</FormControl>
					</FormRow>
					<FormRow>
						<FormControl
							chip="必須"
							isInvalid={!!itemErrors?.renteeChargePer}
							label="借主負担%"
						>
							<Controller
								control={control}
								name={`${fieldPrefix}.renteeChargePer`}
								render={({ field }) => (
									<Input
										max={DEAL_ESTIMATION_OCR_MAX_CHARGE_PER}
										min={DEAL_ESTIMATION_OCR_MIN_CHARGE_PER}
										name={field.name}
										onBlur={() => {
											field.onBlur();
											completeChargePer("renteeChargePer");
										}}
										onChange={(event) => {
											field.onChange(toNumberOrNull(event.target.value));
										}}
										placeholder="借主負担%"
										ref={field.ref}
										step={1}
										type="number"
										value={field.value ?? ""}
									/>
								)}
							/>
							<FormControlText error={itemErrors?.renteeChargePer?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!itemErrors?.ownerChargePer || hasTotalChargePerError}
							label="貸主負担%"
						>
							<Controller
								control={control}
								name={`${fieldPrefix}.ownerChargePer`}
								render={({ field }) => (
									<Input
										max={DEAL_ESTIMATION_OCR_MAX_CHARGE_PER}
										min={DEAL_ESTIMATION_OCR_MIN_CHARGE_PER}
										name={field.name}
										onBlur={() => {
											field.onBlur();
											completeChargePer("ownerChargePer");
										}}
										onChange={(event) => {
											field.onChange(toNumberOrNull(event.target.value));
										}}
										placeholder="貸主負担%"
										ref={field.ref}
										step={1}
										type="number"
										value={field.value ?? ""}
									/>
								)}
							/>
							<FormControlText error={itemErrors?.ownerChargePer?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={
								!!itemErrors?.companyChargePer || hasTotalChargePerError
							}
							label="管理負担%"
						>
							<Controller
								control={control}
								name={`${fieldPrefix}.companyChargePer`}
								render={({ field }) => (
									<Input
										max={DEAL_ESTIMATION_OCR_MAX_CHARGE_PER}
										min={DEAL_ESTIMATION_OCR_MIN_CHARGE_PER}
										name={field.name}
										onBlur={() => {
											field.onBlur();
											completeChargePer("companyChargePer");
										}}
										onChange={(event) => {
											field.onChange(toNumberOrNull(event.target.value));
										}}
										placeholder="管理負担%"
										ref={field.ref}
										step={1}
										type="number"
										value={field.value ?? ""}
									/>
								)}
							/>
							<FormControlText error={itemErrors?.companyChargePer?.message} />
						</FormControl>
					</FormRow>
					<FormRow>
						<FormControl isInvalid={!!itemErrors?.note} label="備考">
							<Controller
								control={control}
								name={`${fieldPrefix}.note`}
								render={({ field }) => (
									<InputText
										maxRows={8}
										name={field.name}
										onBlur={field.onBlur}
										onChange={(event) => {
											field.onChange(event.target.value);
										}}
										placeholder="備考）その他連絡事項など"
										ref={field.ref}
										resize="autoRows"
										rows={2}
										value={field.value}
									/>
								)}
							/>
							<FormControlText error={itemErrors?.note?.message} />
						</FormControl>
					</FormRow>
				</FormGroup>
			)}
		</div>
	);
}
